import React, { useState } from 'react';
import { ChevronDownIcon, ChevronUpIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import faqData from '../../data/tinywins-faq.json';

const FAQMainContent: React.FC = () => {
  const [expandedQuestions, setExpandedQuestions] = useState<Set<string>>(new Set());
  const [searchTerm, setSearchTerm] = useState('');

  const toggleQuestion = (questionId: string) => {
    const newExpanded = new Set(expandedQuestions);
    if (newExpanded.has(questionId)) {
      newExpanded.delete(questionId);
    } else {
      newExpanded.add(questionId);
    }
    setExpandedQuestions(newExpanded);
  };

  // Filter questions based on search term
  const filteredSections = faqData.sections.map(section => ({
    ...section,
    questions: section.questions.filter(question =>
      question.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
      question.answer.toLowerCase().includes(searchTerm.toLowerCase())
    )
  })).filter(section => section.questions.length > 0);

  return (
    <div className="p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">
            Frequently Asked Questions
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Find answers to common questions about TinyWins.ai
          </p>
        </div>

        {/* Search Bar */}
        <div className="mb-6">
          <div className="relative max-w-lg mx-auto">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search questions..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent shadow-sm"
            />
          </div>
        </div>

        {/* FAQ Content */}
        <div className="space-y-6">
          {filteredSections.map((section) => (
            <div key={section.id}>
              {/* Section Title */}
              <h2 className="text-xl font-bold text-gray-900 mb-4 text-left">
                {section.title}
              </h2>
              
              {/* Questions List */}
              <div className="space-y-3">
                {section.questions.map((question, index) => (
                  <div key={question.id} className="bg-white rounded-lg border border-gray-200 shadow-sm">
                    <button
                      onClick={() => toggleQuestion(question.id)}
                      className="w-full px-4 py-3 text-left flex items-center justify-between hover:bg-gray-50 transition-colors rounded-lg"
                    >
                      <h3 className="text-base font-semibold text-gray-900 pr-4">
                        <span className="text-blue-600 font-bold mr-2">Q{index + 1}:</span>
                        {question.question}
                      </h3>
                      <div className="flex-shrink-0">
                        {expandedQuestions.has(question.id) ? (
                          <ChevronUpIcon className="h-4 w-4 text-gray-500" />
                        ) : (
                          <ChevronDownIcon className="h-4 w-4 text-gray-500" />
                        )}
                      </div>
                    </button>
                    
                    {expandedQuestions.has(question.id) && (
                      <div className="px-4 pb-3 border-t border-gray-100">
                        <div className="pt-3 text-gray-700 leading-relaxed text-left">
                          <div className="flex items-start">
                            <span className="text-green-600 font-bold mr-2 mt-1">Answer:</span>
                            <div className="flex-1">
                              {question.answer.split('\n').map((line, lineIndex) => (
                                <p key={lineIndex} className="mb-1 last:mb-0 text-sm">
                                  {line}
                                </p>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
          
          {/* No Results */}
          {filteredSections.length === 0 && searchTerm && (
            <div className="text-center py-12">
              <div className="bg-white rounded-lg border border-gray-200 p-6 max-w-md mx-auto">
                <MagnifyingGlassIcon className="h-10 w-10 text-gray-400 mx-auto mb-3" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No results found</h3>
                <p className="text-gray-600 mb-3">We couldn't find any FAQs matching "{searchTerm}"</p>
                <button
                  onClick={() => setSearchTerm('')}
                  className="text-blue-600 hover:text-blue-800 font-medium underline"
                >
                  Clear search and show all FAQs
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FAQMainContent;
